/**
 * A sortable, paginated list that becomes a card list on narrow screens.
 *
 * The two presentations are both rendered and toggled by CSS, so there is no
 * layout flash on resize and no viewport measurement in JavaScript. The cost
 * is duplicated markup in the DOM; the benefit is that the breakpoint is
 * declarative and cannot disagree with the stylesheet.
 *
 * Plan §7.3: below 768px a table becomes a **card list**, not a smaller table.
 * Twelve columns at 375px are unreadable at any scale, so the degradation
 * chooses the two fields that matter and drops the rest.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.15, §7.3
 */
import { useTranslation } from 'react-i18next';

import { cn } from '../../lib/cn';
import type { SortState } from '../../lib/table-state';

import type { Column } from './data-table-types';
import { Icon } from './Icon';

export interface DataTableProps<Row> {
  columns: readonly Column<Row>[];
  rows: readonly Row[];
  /** Stable identity per row. */
  rowKey: (row: Row) => string;
  /** Accessible name for the table. */
  caption: string;
  sort: SortState | null;
  onSortChange: (field: string) => void;
  /** Invoked when a row is activated by click or Enter. */
  onRowSelect?: (row: Row) => void;
  isLoading?: boolean;
  emptyMessage?: string;
}

/** Maps a sort state onto the `aria-sort` value for a header cell. */
function ariaSortFor(
  sort: SortState | null,
  field: string | undefined,
): 'ascending' | 'descending' | 'none' | undefined {
  if (field === undefined) return undefined;
  if (sort?.field !== field) return 'none';
  return sort.direction === 'asc' ? 'ascending' : 'descending';
}

export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  caption,
  sort,
  onSortChange,
  onRowSelect,
  isLoading = false,
  emptyMessage,
}: DataTableProps<Row>): React.JSX.Element {
  const { t } = useTranslation();

  const cardColumns = columns.filter((column) => column.card !== 'hidden');
  const primary = cardColumns.filter((column) => column.card === 'primary');
  const secondary = cardColumns.filter((column) => column.card !== 'primary');

  if (isLoading) {
    /* `role="status"` so a screen reader hears the wait instead of silence. */
    return (
      <p role="status" className="text-body p-4 text-sm">
        {t('state.loading')}
      </p>
    );
  }

  if (rows.length === 0) {
    return <p className="text-body p-4 text-sm">{emptyMessage ?? t('state.empty')}</p>;
  }

  return (
    <>
      {/* Table: 768px and up. `overflow-x-auto` on the wrapper so a wide table
          scrolls itself rather than the page (plan §7.3). */}
      <div className="hidden overflow-x-auto rounded-md bg-white shadow-sm md:block">
        <table className="w-full border-collapse text-sm">
          {/* Visually hidden but present: a table with no accessible name is
              announced as "table" and nothing more. */}
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr className="border-gray-100 border-b">
              {columns.map((column) => {
                /* Narrowed to a local before the handler closes over it: the
                   branch below already proves it is a string, and an assertion
                   inside that branch would restate what the check established. */
                const sortField = column.sortField ?? '';

                return (
                  <th
                    key={column.key}
                    scope="col"
                    aria-sort={ariaSortFor(sort, column.sortField)}
                    className={cn(
                      /* The prototype's header treatment: the display font,
                         tracked and uppercase, in body grey. Chrome uses
                         Gilroy, not only headings (§4.5). */
                      'text-body font-display px-s3 py-2.5 text-[10px] font-extrabold',
                      'tracking-[0.12em] whitespace-nowrap uppercase',
                      column.numeric ? 'text-right' : 'text-left',
                    )}
                  >
                    {column.sortField === undefined ? (
                      column.header
                    ) : (
                      /* A real button, so the header is Tab-reachable and
                       Enter-activatable without extra key handling. */
                      <button
                        type="button"
                        onClick={() => {
                          onSortChange(sortField);
                        }}
                        className="hover:text-navy-text inline-flex items-center gap-1 uppercase"
                      >
                        {column.header}
                        {sort?.field === column.sortField ? (
                          <Icon
                            name="arrow"
                            className={
                              sort.direction === 'asc' ? '-rotate-90' : 'rotate-90'
                            }
                          />
                        ) : null}
                      </button>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                className={cn(
                  'border-gray-100 border-b last:border-b-0',
                  onRowSelect === undefined ? '' : 'hover:bg-off-white',
                )}
              >
                {columns.map((column, index) => (
                  <td
                    key={column.key}
                    className={cn(
                      'px-s3 py-2.5',
                      column.numeric ? 'text-right' : 'text-left',
                    )}
                  >
                    {/* The activation target is a real BUTTON in the first
                        cell, not a click handler on the <tr>. A row is not
                        natively interactive, so a clickable one has to
                        simulate focus, Enter and the announced role — and a
                        simulation is what breaks. The button carries the row's
                        label, so it is announced as what it opens. */}
                    {index === 0 && onRowSelect !== undefined ? (
                      <button
                        type="button"
                        onClick={() => {
                          onRowSelect(row);
                        }}
                        className="text-navy-text w-full text-left"
                      >
                        {column.cell(row)}
                      </button>
                    ) : (
                      column.cell(row)
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Card list: below 768px. */}
      <ul aria-label={caption} className="flex flex-col gap-2 md:hidden">
        {rows.map((row) => {
          const content = (
            <>
              {primary.map((column) => (
                <span key={column.key} className="text-navy-text text-sm font-semibold">
                  {column.cell(row)}
                </span>
              ))}
              <span className="text-body flex flex-wrap items-center gap-2 text-xs">
                {secondary.map((column) => (
                  <span key={column.key}>{column.cell(row)}</span>
                ))}
              </span>
            </>
          );

          const shell =
            'border-gray-100 flex min-h-11 w-full flex-col gap-1 rounded-md border bg-white p-3 text-left';

          return (
            <li key={rowKey(row)}>
              {/* A real button when selectable, so the whole card is one
                  natively interactive target rather than a div pretending to
                  be one — no simulated focus, Enter handling or role. */}
              {onRowSelect === undefined ? (
                <div className={shell}>{content}</div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    onRowSelect(row);
                  }}
                  className={shell}
                >
                  {content}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}
